/**
 * Safe Markdown for stored texts — Spec 020 D6.
 *
 * People and agents write Markdown; the app used to show it raw. This renderer
 * builds React elements only: nothing from a text is ever parsed as HTML, so a
 * `<script>` in a message stays text.
 *
 * It reads Markdown the way Telegram does (Spec 019, packages/core/notifications/
 * tg_html.py), so one message looks the same in the bot and here: code spans by
 * backtick runs, `\*` escapes, `***`, emphasis within one line, `__x__` and
 * `snake_case` left as written, a heading as a bold line, «☐» / «☑» boxes, and
 * only http(s) addresses as links — any other `[label](target)` reads as
 * «label (target)». Two things the Mini App adds: a path of the app (`/tasks/…`)
 * or an address on this origin opens inside the app, and a table is a table.
 *
 * No regex lookbehind: Telegram on iOS before 16.4 cannot parse one, and a regex
 * it cannot parse takes the whole app down. Every pattern is bounded per line.
 */

import { Fragment, type ReactNode } from "react";
import { Link } from "react-router-dom";

const SPAN = 400; // the longest bold, italic or struck span, as in Spec 019

const WORD = /[\p{L}\p{N}_]/u;
const ESCAPED = /\\([\\`*_{}[\]()#+\-.!~>|])/g;
// One level of parentheses inside the address: …/wiki/Python_(язык).
const LINK = /\[([^[\]\n]+)\]\(((?:[^()\s]|\([^()\s]*\))+)\)/g;
const WEB = /^https?:\/\/\S+$/i;
const UUID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
// Bare addresses and the app's own paths an agent writes without markup — a case
// too (Spec 025). A bare path needs an id, so «GET /tasks/active» in a sentence stays text.
const BARE = new RegExp(
  `https?:\\/\\/[^\\s<>\\uE000]+|\\/(?:tasks|projects|knowledge(?:\\/sources\\/(?:idea|note)|\\/cases)?)\\/${UUID}(?:\\/history)?|\\/chat\\?message=${UUID}`,
  "gi",
);
const APP_PATH = /^\/(?:tasks|projects|knowledge|chat|business|overview|changes|work|tools|ideas|inbox|me|settings)(?:[/?#]\S*)?$/;
const TRAILING = ".,;:!?»\"'*_~";
// A line longer than this is shown as plain text: nobody writes Markdown that long,
// and a pathological line must not stall the chat (PR #13 review, blocker 1).
const LONG_LINE = 10_000;
// Past this a line cannot be a table rule; the rule's optional spaces backtrack.
const LONG_TABLE_RULE = 2_000;

/**
 * An address and what follows it in the sentence: the full stop, a closing
 * quote, and a `)` only when it has no `(` in the address. One pass that counts
 * brackets once — the per-character recount was quadratic.
 */
export function splitTrailing(found: string): [string, string] {
  let open = 0;
  let close = 0;
  for (const ch of found) {
    if (ch === "(") open++;
    else if (ch === ")") close++;
  }
  let end = found.length;
  while (end > 0) {
    const ch = found[end - 1];
    if (TRAILING.includes(ch)) end--;
    else if (ch === ")" && open < close) {
      close--;
      end--;
    } else break;
  }
  return [found.slice(0, end), found.slice(end)];
}
const EMPHASIS = new RegExp(
  [
    `\\*\\*\\*(?=\\S)(.{0,${SPAN - 1}}?\\S)\\*\\*\\*`,
    `\\*\\*(?=\\S)(.{0,${SPAN - 1}}?\\S)\\*\\*`,
    `~~(?=\\S)(.{0,${SPAN - 1}}?\\S)~~`,
    `\\*(?=[^\\s*])(.{0,${SPAN - 1}}?[^\\s*])\\*(?![\\p{L}\\p{N}_*])`,
    `_(?=[^\\s_])(.{0,${SPAN - 1}}?[^\\s_])_(?![\\p{L}\\p{N}_])`,
  ].join("|"),
  "gu",
);
// Holes are marked with a private-use character (Spec 019 uses NUL; lint forbids it in
// a regex). Both are taken out of the text first, so a text cannot forge a hole.
const MARK = "\uE000";
const HOLE = new RegExp(`${MARK}(\\d+)${MARK}`, "g");
const clean = (text: string) => text.split(MARK).join("").split("\u0000").join("");

const linkClass = "text-p-link underline underline-offset-2";

/** Where a link goes: inside the app, out to the web, or nowhere. */
function target(url: string): { app: string } | { web: string } | null {
  if (APP_PATH.test(url)) return { app: url };
  if (!WEB.test(url)) return null;
  if (typeof window !== "undefined") {
    try {
      const parsed = new URL(url);
      if (parsed.origin === window.location.origin && APP_PATH.test(parsed.pathname)) {
        return { app: parsed.pathname + parsed.search + parsed.hash };
      }
    } catch {
      /* not a URL after all: still a web link, the browser decides */
    }
  }
  return { web: url };
}

function anchor(to: { app: string } | { web: string }, children: ReactNode, key: string, breakAll = false): ReactNode {
  const className = breakAll ? `break-all ${linkClass}` : linkClass;
  return "app" in to ? (
    <Link key={key} to={to.app} className={className}>
      {children}
    </Link>
  ) : (
    <a key={key} href={to.web} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </a>
  );
}

/**
 * Code spans: a run of backticks up to the next run of the same length (Spec 019).
 * A run after a backslash cannot open a span; inside a span a backslash is text.
 */
function codeSpans(line: string, hole: (node: ReactNode) => string): string {
  const runs = [...line.matchAll(/`+/g)].map((m) => [m.index ?? 0, (m.index ?? 0) + m[0].length] as const);
  const following: (number | null)[] = new Array(runs.length).fill(null);
  const seen = new Map<number, number>();
  for (let i = runs.length - 1; i >= 0; i--) {
    const length = runs[i][1] - runs[i][0];
    following[i] = seen.get(length) ?? null;
    seen.set(length, i);
  }
  let out = "";
  let done = 0;
  let i = 0;
  while (i < runs.length) {
    const [start, end] = runs[i];
    const close = following[i];
    if (close === null || (start > 0 && line[start - 1] === "\\")) {
      i++;
      continue;
    }
    let inner = line.slice(end, runs[close][0]);
    if (inner.length > 2 && inner[0] === " " && inner[inner.length - 1] === " ") inner = inner.slice(1, -1);
    out += line.slice(done, start);
    out += hole(
      <code className="rounded bg-p-surface-2 px-1 font-mono text-[0.9em]">{inner}</code>,
    );
    done = runs[close][1];
    i = close + 1;
  }
  return out + line.slice(done);
}

/** Bold, strike and italic of a line whose code and links are already holes. */
function emphasis(text: string, bold: boolean, fill: (text: string, key: string) => ReactNode[], key: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = new RegExp(EMPHASIS.source, EMPHASIS.flags);
  let last = 0;
  let n = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    const start = m.index;
    const before = start > 0 ? text[start - 1] : "";
    // `2*3*4` and `snake_case`: a single marker glued to a word is not emphasis.
    if ((m[4] !== undefined && (WORD.test(before) || before === "*")) ||
        (m[5] !== undefined && WORD.test(before))) {
      re.lastIndex = start + 1;
      continue;
    }
    if (start > last) out.push(...fill(text.slice(last, start), `${key}t${n}`));
    const k = `${key}e${n++}`;
    if (m[1] !== undefined) {
      const inner = emphasis(m[1], bold, fill, k);
      out.push(bold ? <strong key={k}><em>{inner}</em></strong> : <em key={k}>{inner}</em>);
    } else if (m[2] !== undefined) {
      const inner = emphasis(m[2], bold, fill, k);
      out.push(bold ? <strong key={k}>{inner}</strong> : <span key={k}>{inner}</span>);
    } else if (m[3] !== undefined) {
      out.push(<s key={k}>{emphasis(m[3], bold, fill, k)}</s>);
    } else {
      out.push(<em key={k}>{emphasis(m[4] ?? m[5], bold, fill, k)}</em>);
    }
    last = start + m[0].length;
  }
  if (last < text.length) out.push(...fill(text.slice(last), `${key}t${n}`));
  return out;
}

/**
 * One line of Markdown as inline React nodes. `bold: false` is a heading's
 * content: it is bold already, so `**` inside it only drops its markers.
 */
export function renderInline(line: string, key = "i", { bold = true } = {}): ReactNode[] {
  if (line.length > LONG_LINE) return [clean(line)];
  const holes: ReactNode[] = [];
  const hole = (node: ReactNode) => `${MARK}${holes.push(node) - 1}${MARK}`;
  // A hole may hold text with holes of its own («label (target)» around a code span).
  const fill = (part: string, k: string): ReactNode[] => {
    const out: ReactNode[] = [];
    let last = 0;
    for (const m of part.matchAll(HOLE)) {
      const at = m.index ?? 0;
      if (at > last) out.push(part.slice(last, at));
      const node = holes[Number(m[1])];
      if (typeof node === "string") out.push(...fill(node, `${k}h${m[1]}`));
      else out.push(<Fragment key={`${k}h${m[1]}`}>{node}</Fragment>);
      last = at + m[0].length;
    }
    if (last < part.length) out.push(part.slice(last));
    return out;
  };

  let text = codeSpans(clean(line), hole);
  text = text.replace(ESCAPED, (_, ch: string) => hole(ch));
  text = text.replace(LINK, (_, label: string, url: string) => {
    const to = target(url);
    if (!to) return hole(label === url ? label : `${label} (${url})`);
    const k = `${key}a${holes.length}`;
    return hole(anchor(to, emphasis(label, bold, fill, k), k));
  });
  text = text.replace(BARE, (found: string, offset: number, whole: string) => {
    const before = offset > 0 ? whole[offset - 1] : "";
    if (found.startsWith("/") && before && !/[\s(«"'\uE000]/.test(before)) return found;
    const [url, tail] = splitTrailing(found);
    const to = target(url);
    return (to ? hole(anchor(to, url, `${key}u${holes.length}`, true)) : url) + tail;
  });
  return emphasis(text, bold, fill, key);
}

// A fence and its language, checked by hand: the regex form backtracks quadratically
// on a long line ending in a backtick (Spec 019 uses possessive quantifiers here).
const FENCE_OPEN = /^ {0,3}(`{3,}|~{3,})/;
const FENCE_LANG = /^[ \t]*([\w+#.-]*)/;

function fenceOf(line: string): { fence: string; lang: string } | null {
  const m = FENCE_OPEN.exec(line);
  if (!m) return null;
  const rest = line.slice(m[0].length);
  if (rest.includes("`")) return null;
  return { fence: m[1], lang: FENCE_LANG.exec(rest)![1] };
}
const QUOTE = /^ {0,3}> ?(.*)$/;
const HEADING = /^ {0,3}#{1,6}[ \t]+(.*)$/;
const RULE = /^ {0,3}([-*_])(?: *\1){2,} *$/;
const LIST = /^(\s*)(?:([-*+])|(\d{1,9})[.)])\s+(.*)$/;
const CHECKBOX = /^\[([ xX])\]\s+/;
const TABLE_ROW = /^\s*\|.*\|\s*$/;
const TABLE_RULE = /^ *\|? *:?-{3,}:? *(?:\| *:?-{3,}:? *)*\|? *$/;
const isTableRule = (line: string) => line.length <= LONG_TABLE_RULE && TABLE_RULE.test(line);

/** The text of an ATX heading without its closing `#`s (Spec 019). */
function heading(line: string): string | null {
  const m = HEADING.exec(line);
  if (!m) return null;
  let content = m[1].trimEnd();
  const bare = content.replace(/#+$/, "");
  if (bare !== content && (!bare || /[ \t]$/.test(bare))) content = bare.trimEnd();
  return content;
}

/** A bare fence of the same kind and at least as long ends a code block. */
function closes(line: string, fence: string): boolean {
  const m = fenceOf(line);
  return !!m && !m.lang && m.fence.startsWith(fence);
}

function startsBlock(line: string): boolean {
  return !!fenceOf(line) || RULE.test(line) || HEADING.test(line) || QUOTE.test(line) ||
    LIST.test(line) || TABLE_ROW.test(line);
}

/** Markdown text → React blocks. Empty or missing text gives an empty list. */
export function renderMarkdown(src: string | null | undefined): ReactNode[] {
  // U+2028 / U+2029 end a line too: left in, `.` stops at them and the block
  // patterns backtrack (review item 6).
  const lines = String(src ?? "").replace(/\r\n?|[\u2028\u2029]/g, "\n").split("\n").map(clean);
  const blocks: ReactNode[] = [];
  let i = 0;
  let n = 0;
  const key = () => `b${n++}`;
  const withBreaks = (rows: string[], k: string) =>
    rows.flatMap((row, j) => [...(j ? [<br key={`${k}br${j}`} />] : []), ...renderInline(row, `${k}-${j}`)]);

  while (i < lines.length) {
    const line = lines[i];

    const fence = fenceOf(line);
    if (fence) {
      const code: string[] = [];
      i++;
      while (i < lines.length && !closes(lines[i], fence.fence)) code.push(lines[i++]);
      i++;
      const body = code.join("\n").replace(/^\n+|\n+$/g, "");
      if (body) {
        blocks.push(
          <pre key={key()} className="overflow-x-auto rounded-compact bg-p-surface-2 px-3 py-2 font-mono text-[13px] leading-5">
            <code>{body}</code>
          </pre>,
        );
      }
      continue;
    }
    if (!line.trim()) {
      i++;
      continue;
    }
    if (RULE.test(line)) {
      blocks.push(<hr key={key()} className="border-p-border" />);
      i++;
      continue;
    }
    const title = heading(line);
    if (title !== null) {
      const k = key();
      blocks.push(<p key={k} className="font-semibold">{renderInline(title, k, { bold: false })}</p>);
      i++;
      continue;
    }
    if (QUOTE.test(line)) {
      const quoted: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i])) quoted.push(QUOTE.exec(lines[i++])![1]);
      const k = key();
      blocks.push(
        <blockquote key={k} className="border-l-[3px] border-p-border-strong pl-3 text-p-text-muted">
          {withBreaks(quoted, k)}
        </blockquote>,
      );
      continue;
    }
    const item = LIST.exec(line);
    if (item) {
      const ordered = item[3] !== undefined;
      const items: RegExpExecArray[] = [];
      let next: RegExpExecArray | null;
      while (i < lines.length && (next = LIST.exec(lines[i])) && (next[3] !== undefined) === ordered) {
        items.push(next);
        i++;
      }
      const k = key();
      const children = items.map((it, j) => {
        let text = it[4];
        const box = CHECKBOX.exec(text);
        if (box) text = text.slice(box[0].length);
        // Nesting reads as an indent; a sub-list is a list of its own in no text we keep.
        const depth = Math.min(3, Math.floor(it[1].replace(/\t/g, "  ").length / 2));
        return (
          <li key={j} className={box ? "list-none" : undefined} style={depth ? { marginLeft: `${depth}rem` } : undefined}>
            {box && <span aria-hidden="true" className="-ml-5 mr-1 inline-block w-4">{box[1] === " " ? "☐" : "☑"}</span>}
            {renderInline(text, `${k}-${j}`)}
          </li>
        );
      });
      blocks.push(
        ordered ? (
          <ol key={k} start={Number(items[0][3])} className="list-decimal space-y-0.5 pl-5">{children}</ol>
        ) : (
          <ul key={k} className="list-disc space-y-0.5 pl-5">{children}</ul>
        ),
      );
      continue;
    }
    if (TABLE_ROW.test(line)) {
      const rows: string[] = [];
      while (i < lines.length && TABLE_ROW.test(lines[i])) rows.push(lines[i++]);
      const cells = rows
        .filter((r) => !isTableRule(r))
        .map((r) => r.trim().slice(1, -1).split("|").map((c) => c.trim()));
      const [head, ...body] = cells;
      const k = key();
      blocks.push(
        <div key={k} className="overflow-x-auto">
          <table className="border-collapse text-[14px] leading-5">
            <thead>
              <tr>
                {(head ?? []).map((c, j) => (
                  <th key={j} className="border border-p-border bg-p-surface-2 px-2 py-1 text-left font-semibold">
                    {renderInline(c, `${k}h${j}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((row, r) => (
                <tr key={r}>
                  {row.map((c, j) => (
                    <td key={j} className="border border-p-border px-2 py-1 align-top">
                      {renderInline(c, `${k}r${r}c${j}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    const paragraph: string[] = [line];
    i++;
    while (i < lines.length && lines[i].trim() && !startsBlock(lines[i])) paragraph.push(lines[i++]);
    const k = key();
    blocks.push(<p key={k}>{withBreaks(paragraph, k)}</p>);
  }
  return blocks;
}

/**
 * The same text renders to the same elements: parsed once and kept (PR 021
 * review, item 1 — a feed of 50 messages re-parsed on every refetch). React
 * elements are immutable, so sharing them between renders is safe. No hook,
 * so the component stays a plain function.
 */
const RENDERED = new Map<string, ReactNode[]>();
const RENDERED_MAX = 400;
function rendered(text: string): ReactNode[] {
  const hit = RENDERED.get(text);
  if (hit) {
    RENDERED.delete(text);
    RENDERED.set(text, hit);
    return hit;
  }
  const nodes = renderMarkdown(text);
  RENDERED.set(text, nodes);
  if (RENDERED.size > RENDERED_MAX) RENDERED.delete(RENDERED.keys().next().value as string);
  return nodes;
}

/** A stored Markdown text as readable formatting. */
export function Markdown({ text, className }: { text: string | null | undefined; className?: string }) {
  return <div className={["space-y-1.5 break-words", className].filter(Boolean).join(" ")}>{rendered(String(text ?? ""))}</div>;
}

/**
 * A message as one line of plain text, for previews (PC audit 28.09, point 6).
 * Markers go, words stay: «test_agent_stats_db» and «DEPLOYED_COMMIT» keep their
 * underscores; `code` keeps its text; [text](link) keeps the text.
 */
export function plainText(src: string | null | undefined): string {
  return (src ?? "")
    .replace(/```[^\n]*\n?/g, " ")
    .replace(/`([^`]*)`/g, "$1")
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/^\s{0,3}(#{1,6}|>+|[-*+]|\d+[.)])\s+/gm, "")
    .replace(/(\*\*|__)(?=\S)([\s\S]*?\S)\1/g, "$2")
    .replace(/(^|[^\w*])\*(?=\S)([^*\n]*?\S)\*(?![\w*])/g, "$1$2")
    .replace(/(^|[^\w])_(?=\S)([^_\n]*?\S)_(?!\w)/g, "$1$2")
    .replace(/~~(?=\S)([\s\S]*?\S)~~/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}
